import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Star, Heart, Clock, Shield, CheckCircle2 } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
const TabSection = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab ?? 0);
  const tabs = [{
    id: 'About Center',
    title: 'About Our Center',
    subtitle: 'Learn about us',
    quote: 'Caring for seniors is a labor of love and requires a special person',
    content: ["Neither giving nor receiving care is easy. Elisabeth's House Assisted Living Centre in Hagerstown, Maryland puts relationships first to support those giving care, to protect the dignity of those receiving it, and to enhance the quality of life for both seniors and their families.", "Elisabeth's House Assisted Living Centre in Hagerstown, Maryland, offers affordable Group care services 24 hours a day, 365 days a year for those who need it. We are about the most affordable Group home in Hagerstown, Maryland.", 'Call us today to work with a care coordinator at our local office.'],
    image: 'https://elisabethhouse.net/wp-content/uploads/2020/04/4.png',
    cta: 'Contact us'
  }, {
    id: 'Main Approach',
    title: 'Main Approach',
    subtitle: 'Our Philosophy',
    quote: 'Caring for seniors is a labor of love and requires a special person',
    content: ["Everything we do is driven by our universal mission: To enhance the lives of ageing adults and their families. The solution for families that need an extra level of care. Elisabeth's House Assisted Living Center, Hagerstown, is among the best in Hagerstown, Maryland.", 'A sense of home can become especially important as we age. Our in-home assisted living services help ease the challenges of ageing while encouraging seniors to enjoy the comforts of home.'],
    image: 'https://elisabethhouse.net/wp-content/uploads/2017/10/bg-23-copyright.jpg',
    cta: 'Contact us'
  }, {
    id: 'What We Offer',
    title: 'What We Offer',
    subtitle: 'Our Services',
    quote: 'Caring for seniors is a labor of love and requires a special person',
    content: ["Through more than 10 years of providing innovative solutions that improve health and quality of life for those in need of in-home health services, Elisabeth's House Assisted Living in Hagerstown, Maryland – has established itself as one of the most experienced Assisted Living company in the industry.", 'Our approach to quality care focuses on a commitment to providing a level of expertise, training, patient service, and monitoring that is difficult to match in our industry.'],
    image: 'https://elisabethhouse.net/wp-content/uploads/2020/04/4.png',
    cta: 'Contact us'
  }, {
    id: 'How We Work',
    title: 'How We Work',
    subtitle: 'Our Process',
    quote: 'Caring for seniors is a labor of love and requires a special person',
    content: ['Elderly care, or simply eldercare (also known as aged care), is the fulfillment of the special needs and requirements that are unique to senior citizens. This broad term encompasses such services as assisted living, adult day care, long-term care, nursing homes, hospice care, and home care.', 'Because of the wide variety of elderly care found in Hagerstown, Maryland and nationally, as well as differentiating regional perspectives on elderly citizens, it cannot be limited to any single practice.'],
    image: 'https://elisabethhouse.net/wp-content/uploads/2020/04/happy-senior-couple-wearing-party-hats-with-friend-A8SW29F-scaled.jpg',
    cta: 'Contact Us'
  }];
  return <section className="py-16 md:py-24 bg-white overflow-hidden" data-id="element-215">
      <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-216">
        <div className="text-center mb-12 md:mb-16" data-id="element-217">
          <span className="text-warmCoral tracking-[0.2em] uppercase text-sm font-medium mb-4 block" data-id="element-218">
            Learn about us
          </span>
          <h2 className="text-3xl md:text-5xl font-serif" data-id="element-219">About Our Center</h2>
        </div>

        <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-12 md:mb-16 border-b border-warmGray/10 pb-8" data-id="element-220">
          {tabs.map((tab, i) => <button key={tab.id} onClick={() => setActiveTab(i)} className={cn('text-base md:text-lg font-serif px-4 md:px-6 py-2 transition-all relative', activeTab === i ? 'text-warmCoral' : 'text-charcoal/40 hover:text-charcoal/70')} data-id="element-221">
              {tab.id}
              {activeTab === i && <motion.div layoutId="about-tab-underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-warmCoral" data-id="element-222" />}
            </button>)}
        </div>

        <div className="grid lg:grid-cols-2 gap-12 md:gap-20 items-center min-h-[500px] md:min-h-[600px]" data-id="element-223">
          <AnimatePresence mode="wait" data-id="element-224">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} transition={{
            duration: 0.5
          }} className="text-center lg:text-left" data-id="element-225">
              <div className="mb-8" data-id="element-226">
                <span className="text-warmCoral font-accent text-2xl md:text-3xl italic block mb-6" data-id="element-227">
                  "{tabs[activeTab].quote}"
                </span>
                <div className="space-y-4 md:space-y-6 text-charcoal/70 text-base md:text-lg leading-relaxed" data-id="element-228">
                  {tabs[activeTab].content.map((p, idx) => <p key={idx} data-id="element-229">{p}</p>)}
                </div>
              </div>
              <Link to="/contact" className="bg-charcoal text-white px-10 py-4 rounded-full font-medium hover:bg-warmCoral transition-all shadow-xl inline-flex items-center space-x-2 group w-full sm:w-auto justify-center" data-id="element-230">
                <span data-id="element-231">{tabs[activeTab].cta}</span>
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-232" />
              </Link>
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode="wait" data-id="element-233">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            scale: 1.05
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.7
          }} className="relative aspect-[4/5] rounded-[32px] md:rounded-[40px] overflow-hidden shadow-2xl" data-id="element-234">
              <img src={tabs[activeTab].image} alt={tabs[activeTab].id} className="w-full h-full object-cover" data-id="element-235" />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/20 to-transparent" data-id="element-236" />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
};
const WhyChooseUs = () => {
  const reasons = [{
    title: 'Get Back to Your Life',
    desc: 'Our in-house rehabilitation services use the latest advances in technology to ensure our residents receive the highest level of service available.',
    icon: Heart
  }, {
    title: '24/7 Availability',
    desc: 'Whether it be about therapy options, nursing solutions, the admissions process, or insurance questions, we are available to answer any questions.',
    icon: Clock
  }, {
    title: 'Excellent Quality of Life',
    desc: 'We have an array of social, recreational and religious activities within our bright and cheery surroundings that allow residents to enjoy their stay.',
    icon: Star
  }];
  return <section className="py-16 md:py-24 bg-warmGray/5" data-id="element-237">
      <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-238">
        <div className="text-center mb-12 md:mb-20" data-id="element-239">
          <span className="text-warmCoral tracking-[0.2em] uppercase text-sm font-medium mb-4 block" data-id="element-240">
            Why choose us
          </span>
          <h2 className="text-3xl md:text-5xl font-serif max-w-3xl mx-auto leading-tight" data-id="element-241">
            Progressive Care Centers are the Right Choice
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12" data-id="element-242">
          {reasons.map((reason, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.1
        }} viewport={{
          once: true
        }} whileHover={{
          y: -10
        }} className="bg-white p-8 md:p-12 rounded-[32px] md:rounded-[40px] shadow-sm border border-warmGray/10 transition-all duration-500 text-center sm:text-left" data-id="element-243">
              <div className={cn('w-16 h-16 rounded-2xl flex items-center justify-center mb-8 mx-auto sm:mx-0', i % 2 === 0 ? 'bg-softPeach/20 text-warmCoral' : 'bg-lightBlue/20 text-lightBlue')} data-id="element-244">
                <reason.icon className="w-8 h-8" data-id="element-245" />
              </div>
              <h3 className="text-charcoal text-xl md:text-2xl font-serif mb-4 md:mb-6" data-id="element-246">
                {reason.title}
              </h3>
              <p className="text-charcoal/60 leading-relaxed text-base md:text-lg" data-id="element-247">
                {reason.desc}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
const Testimonials = () => {
  const reviews = [{
    name: 'Katie Elliott',
    age: '37 years',
    quote: 'I cannot thank you enough (Elisabeth House, Hagerstown, Maryland) for your understanding and support through some difficult times. I would highly recommend your company to anyone. Your company has made our life easier.',
    image: 'https://elisabethhouse.net/wp-content/uploads/2017/10/testi-3-copyright.jpg'
  }, {
    name: 'Renee Sherman',
    age: '37 years',
    quote: 'I was visiting a number of assisted living facilities last month in Hagerstown, Maryland, and was really impressed by the knowledge of this one. They have professional caregivers.',
    image: 'https://elisabethhouse.net/wp-content/uploads/2017/10/testi-2-copyright.jpg'
  }, {
    name: 'Jay Lian',
    age: '37 years',
    quote: "Elisabeth's House, Hagerstown, Maryland is the best! The team of professional caretakers have helped us to improve many aspects of our life, social life included. The personal assistant works hard and knows what they are doing. Thank you.",
    image: 'https://elisabethhouse.net/wp-content/uploads/2017/10/testi-1-copyright.jpg'
  }];
  return <section className="py-16 md:py-24 bg-charcoal text-white overflow-hidden" data-id="element-248">
      <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-249">
        <div className="text-center mb-12 md:mb-20" data-id="element-250">
          <span className="text-warmCoral tracking-[0.2em] uppercase text-sm font-medium mb-4 block" data-id="element-251">
            Testimonials
          </span>
          <h2 className="text-3xl md:text-5xl font-serif" data-id="element-252">What People Say</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-253">
          {reviews.map((review, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.1
        }} className="bg-white/5 backdrop-blur-lg border border-white/10 p-8 md:p-10 rounded-[32px] md:rounded-[40px] flex flex-col" data-id="element-254">
              <div className="flex text-warmCoral mb-8" data-id="element-255">
                {[...Array(5)].map((_, idx) => <Star key={idx} className="w-4 h-4 fill-current" data-id="element-256" />)}
              </div>
              <p className="text-xl font-serif italic mb-10 leading-relaxed text-white/80 flex-grow" data-id="element-257">
                "{review.quote}"
              </p>
              <div className="flex items-center space-x-4 pt-8 border-t border-white/10" data-id="element-258">
                <img src={review.image} alt={review.name} className="w-16 h-16 rounded-full object-cover grayscale" data-id="element-259" />
                <div data-id="element-260">
                  <div className="font-serif text-lg" data-id="element-261">{review.name}</div>
                  <div className="text-white/40 text-sm uppercase tracking-widest" data-id="element-262">
                    {review.age}
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
const ValuesSection = () => {
  return <section className="py-16 md:py-24 bg-white" data-id="element-263">
      <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-264">
        <div className="bg-warmDark rounded-[40px] md:rounded-[60px] p-10 md:p-24 relative overflow-hidden" data-id="element-265">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-warmCoral/5 -skew-x-12 transform translate-x-1/4 hidden md:block" data-id="element-266" />

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="relative z-10 max-w-3xl text-center md:text-left" data-id="element-267">
            <span className="text-warmCoral tracking-[0.2em] uppercase text-sm font-medium mb-6 block" data-id="element-268">
              Company Profile
            </span>
            <h2 className="text-white text-3xl md:text-6xl font-serif mb-6 md:mb-10" data-id="element-269">
              Our Values
            </h2>
            <p className="text-white/70 text-lg md:text-xl leading-relaxed mb-8 md:mb-12" data-id="element-270">
              We are committed to the residents and communities we serve to
              promote better and healthier lives. We strive to be an industry
              leader with uncompromising standards measured by defined clinical
              outcomes, customer service programs and financial sustainability.
            </p>
            <Link to="/contact" className="w-full sm:w-auto bg-warmCoral text-white px-12 py-5 rounded-full text-lg font-medium hover:bg-white hover:text-charcoal transition-all shadow-2xl inline-block" data-id="element-271">
              Contact Us Today
            </Link>
          </motion.div>
        </div>
      </div>
    </section>;
};
export const AboutUs = () => {
  return <div className="min-h-screen bg-white" data-id="element-272">
      {/* Page Header */}
      <section className="relative h-[40vh] md:h-[50vh] min-h-[350px] md:min-h-[400px] flex items-center overflow-hidden pt-24" data-id="element-273">
        <div className="absolute inset-0 z-0" data-id="element-274">
          <img src="https://images.unsplash.com/photo-1516733725897-1aa73b87c8e8?auto=format&fit=crop&q=80&w=2000" alt="About Elisabeth House" className="w-full h-full object-cover" data-id="element-275" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/50 to-charcoal/20" data-id="element-276" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full" data-id="element-277">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="text-center md:text-left" data-id="element-278">
            <div className="flex items-center justify-center md:justify-start space-x-3 text-white/60 text-xs uppercase tracking-[0.3em] mb-6 md:mb-8" data-id="element-279">
              <Link to="/" className="hover:text-warmCoral transition-colors" data-id="element-280">
                Home
              </Link>
              <span className="w-1 h-1 bg-warmCoral rounded-full" data-id="element-281" />
              <span className="text-white" data-id="element-282">About Us</span>
            </div>

            <div className="relative inline-block" data-id="element-283">
              <h1 className="text-white text-4xl md:text-7xl font-serif leading-none mb-4" data-id="element-284">
                About Us
              </h1>
              <motion.span initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.5,
              duration: 0.8
            }} className="absolute -top-12 md:-top-16 -right-4 md:-right-8 text-warmCoral font-accent text-3xl md:text-5xl rotate-[-12deg] hidden sm:block" data-id="element-285">
                Our Story
              </motion.span>
            </div>

            <p className="text-white/80 text-lg md:text-2xl max-w-2xl font-light leading-relaxed mt-6 md:mt-8 border-l-2 border-warmCoral pl-6 md:pl-8 mx-auto md:mx-0" data-id="element-286">
              A legacy of care, dignity, and unparalleled hospitality in the
              heart of Hagerstown.
            </p>
          </motion.div>
        </div>
      </section>

      <TabSection data-id="element-287" />
      <WhyChooseUs data-id="element-288" />
      <Testimonials data-id="element-289" />
      <ValuesSection data-id="element-290" />
    </div>;
};